import React, { useState } from 'react'

import { Dialog, Field, Layout, Text, TextField } from '@loadsmart/miranda-react'

import { LOGIN_STORAGE_USER } from 'auth/domain/User'
import { AnalyticsEvent } from 'core/analytics/events'
import { useAnalytics } from 'core/analytics/useAnalytics'
import { LocalStorageClient } from 'core/infra/storage/LocalStorageClient'

export type TalkToSalesDialogProps = {
  isOpen: boolean
  onClose: () => void
  userUuid: string
  scheduleUuid: string
  schedule?: any
  onLeadSubmit?: (values: { email: string; name: string; company: string; phone: string }) => void
  reportName?: string
  reportId?: string
  roleName?: string
}

type UserLogin = {
  client: string
  first_name: string
  last_name: string
  email: string
  issuer: string
}

export function TalkToSalesDialog({
  isOpen,
  onClose,
  userUuid,
  scheduleUuid,
  schedule,
  onLeadSubmit,
  reportName,
  reportId,
  roleName,
}: TalkToSalesDialogProps) {
  const { track } = useAnalytics()
  const hasTrackedOpen = React.useRef(false)

  // Track when lead form opens (only once per dialog instance)
  React.useEffect(() => {
    if (isOpen && !hasTrackedOpen.current) {
      hasTrackedOpen.current = true
      track(AnalyticsEvent.OpenLeadForm, {
        'Report Name': reportName || '',
        'Report Id': reportId || '',
        'Role Name': roleName || '',
        'Is First Schedule': true,
        'Lead Form Type': 'Talk to Sales',
      })
    }
    
    // Reset tracking when dialog closes
    if (!isOpen) {
      hasTrackedOpen.current = false
    }
  }, [isOpen, track, reportName, reportId, roleName])

  // Create a cancel handler that tracks analytics
  const handleCancel = () => {
    track(AnalyticsEvent.CancelLeadForm, {
      'Report Name': reportName || '',
      'Report Id': reportId || '',
      'Role Name': roleName || '',
      'Is First Schedule': true,
      'Lead Form Type': 'Talk to Sales',
    })
    onClose()
  }

  return (
    <Dialog open={isOpen} onClose={handleCancel} size="extra-large" data-testid="talk-to-sales-dialog">
      <DialogContent
        onClose={handleCancel}
        userUuid={userUuid}
        scheduleUuid={scheduleUuid}
        schedule={schedule}
        onLeadSubmit={onLeadSubmit}
        reportName={reportName}
        reportId={reportId}
        roleName={roleName}
      />
    </Dialog>
  )
}

type DialogContentProps = {
  onClose: () => void
  userUuid: string
  scheduleUuid: string
  schedule?: any
  onLeadSubmit?: (values: { email: string; name: string; company: string; phone: string }) => void
  reportName?: string
  reportId?: string
  roleName?: string
}

function DialogContent({ onClose, onLeadSubmit, reportName, reportId, roleName }: DialogContentProps) {
  const storage = new LocalStorageClient()
  const user_login = storage.get(LOGIN_STORAGE_USER) as UserLogin
  const { track } = useAnalytics()

  const [values, setValues] = useState({
    email: user_login?.email || '',
    name: `${user_login?.first_name} ${user_login?.last_name}`.trim(),
    company: '',
    phone: '',
  })

  const [errors, setErrors] = useState<Partial<typeof values>>({})

  const validate = () => {
    const newErrors: Partial<typeof values> = {}

    if (!values.email.includes('@')) {
      newErrors.email = 'Invalid email address'
    }
    if (!values.name.trim()) {
      newErrors.name = 'Name is required'
    }
    if (!values.company.trim()) {
      newErrors.company = 'Company is required'
    }
    if (!values.phone.trim()) {
      newErrors.phone = 'Phone number is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (field: keyof typeof values, value: string) => {
    setValues(prev => ({ ...prev, [field]: value }))
    setErrors(prev => ({ ...prev, [field]: '' }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    
    // Track lead form submission
    track(AnalyticsEvent.SubmitLeadForm, {
      'Report Name': reportName || '',
      'Report Id': reportId || '',
      'Role Name': roleName || '',
      'Is First Schedule': true,
      'Lead Form Type': 'Talk to Sales',
      'Lead Email': values.email,
      'Lead Company': values.company,
      'Has Phone': !!values.phone.trim(),
    })
    
    if (typeof onLeadSubmit === 'function') {
      onLeadSubmit({
        email: values.email,
        name: values.name,
        company: values.company,
        phone: values.phone,
      })
    }
    onClose()
  }


  return (
    <>
      <Dialog.Header>Let’s talk about next steps.</Dialog.Header>
      <Dialog.Body>
        <Layout.Stack>
          <Text variant="body-md-bold" color="color-text-secondary">
            Enter your work email to get started.
          </Text>
          <form id="talk-to-sales-form" onSubmit={handleSubmit}>
            <Layout.Stack gap="spacing-4">
              <Field status={errors.email ? 'danger' : 'default'}>
                <Field.Label id="email-label">Work email</Field.Label>
                <TextField
                  aria-labelledby="email-label"
                  name="email"
                  placeholder="Enter work email"
                  value={values.email}
                  onChange={e => handleChange('email', e.target.value)}
                  style={{ width: '100%' }}
                />
                <Field.Hint>{errors.email}</Field.Hint>
              </Field>
              <Field status={errors.name ? 'danger' : 'default'}>
                <Field.Label id="name-label">Name</Field.Label>
                <TextField
                  aria-labelledby="name-label"
                  name="name"
                  placeholder="Enter your name"
                  value={values.name}
                  onChange={e => handleChange('name', e.target.value)}
                  style={{ width: '100%' }}
                />
                <Field.Hint>{errors.name}</Field.Hint>
              </Field>
              <Field status={errors.company ? 'danger' : 'default'}>
                <Field.Label id="company-label">Company</Field.Label>
                <TextField
                  aria-labelledby="company-label"
                  name="company"
                  placeholder="Enter your company"
                  value={values.company}
                  onChange={e => handleChange('company', e.target.value)}
                  style={{ width: '100%' }}
                />
                <Field.Hint>{errors.company}</Field.Hint>
              </Field>
              <Field status={errors.phone ? 'danger' : 'default'}>
                <Field.Label id="phone-label">Phone number</Field.Label>
                <TextField
                  aria-labelledby="phone-label"
                  name="phone"
                  placeholder="Enter your phone number"
                  value={values.phone}
                  onChange={e => handleChange('phone', e.target.value)}
                  style={{ width: '100%' }}
                />
                <Field.Hint>{errors.phone}</Field.Hint>
              </Field>
            </Layout.Stack>
          </form>
          <Text variant="body-sm" color="color-text-tertiary">
            Your information will be used solely by our team to follow up on your request.
          </Text>
        </Layout.Stack>
      </Dialog.Body>
      <Dialog.Actions>
        <Dialog.ActionTertiary onClick={onClose}>Cancel</Dialog.ActionTertiary>
        <Dialog.ActionPrimary type="submit" form="talk-to-sales-form">
          Submit
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </>
  )
}
